import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchCapterraReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId,widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchCapterraReview(payload);
                } else {
                    let app_type = 'Capterra_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`} style={dynamicStyle}>
             {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
                {/* Show Header Section Accordgin to condition*/}
                {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                    <>
                    {Number(header_is_widget_title) === 1 && (
                        <>
                            <div className="upgadeify-capterra-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                            <div className="upgadeify-capterra-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                        </>
                    )}
                    {Number(header_is_show_header) === 1 &&
                        (Number(header_source_icon) === 1 ||
                            Number(header_rating) === 1 ||
                            Number(header_number_of_reviews) === 1 ||
                            Number(header_write_reviews_button) === 1) && (
                            <div className="upgadeify-capterra-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                        
                                <div className='upgadeify-capterra-reviews-header-left'>
                                    {Number(header_source_icon) === 1 && (
                                    <div className='upgadeify-capterra-reviews-header-title'>
                                       <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 106 36" className="injected-svg"><g clip-path="url(#a-1)"><path fill="#FF9D28" d="M0 14.395h15.594V8.557L0 14.395Z"></path><path fill="#68C5ED" d="M15.594 8.557v20.638l7.373-23.39-7.373 2.752Z"></path><path fill="#044D80" d="M15.594 14.395H9.702l5.892 14.783V14.395Z"></path><path fill="#E54747" d="m0 14.395 11.219 3.793-1.517-3.793H0Z"></path><path fill="#044D80" d="M35.739 23.745c-.089.088-.23.194-.424.317a4.518 4.518 0 0 1-.776.388 6.483 6.483 0 0 1-1.182.335 8.424 8.424 0 0 1-1.587.141c-1.147 0-2.152-.194-3-.546-.846-.37-1.534-.882-2.08-1.553a6.3 6.3 0 0 1-1.2-2.346c-.265-.9-.388-1.905-.388-2.981 0-1.076.14-2.082.423-2.999.282-.917.688-1.71 1.253-2.381a5.613 5.613 0 0 1 2.081-1.57c.83-.37 1.817-.565 2.946-.565.547 0 1.058.053 1.5.142.44.105.846.211 1.181.352.335.141.618.283.83.424.229.14.387.282.493.37.124.106.247.247.335.406.089.159.142.335.142.511 0 .283-.106.565-.318.847a5.987 5.987 0 0 1-.706.794 8.69 8.69 0 0 0-.582-.53 4.272 4.272 0 0 0-.723-.476 4.15 4.15 0 0 0-.9-.335 4.39 4.39 0 0 0-1.129-.14c-.723 0-1.34.14-1.834.44s-.882.67-1.182 1.147a4.78 4.78 0 0 0-.635 1.605 9.16 9.16 0 0 0-.194 1.852c0 .67.07 1.306.194 1.923.123.617.353 1.146.67 1.623.318.476.724.846 1.235 1.129.512.282 1.129.423 1.887.423a4.22 4.22 0 0 0 1.094-.141c.335-.106.635-.23.882-.37.265-.142.477-.3.67-.441.195-.16.353-.3.477-.406.088.106.176.23.3.37.123.141.229.3.317.441.106.159.177.318.265.477a.999.999 0 0 1 0 .9 2.89 2.89 0 0 1-.335.423Zm11.007-1.641c0 1.129.159 1.958.494 2.47a3.274 3.274 0 0 1-.723.211 4.48 4.48 0 0 1-.706.07c-.511 0-.9-.105-1.164-.299-.265-.194-.441-.494-.512-.864-.317.335-.723.617-1.252.846-.512.23-1.147.335-1.888.335a4.41 4.41 0 0 1-1.164-.158 2.99 2.99 0 0 1-1.058-.53c-.318-.246-.565-.564-.776-.97-.194-.405-.3-.9-.3-1.481 0-.724.176-1.323.529-1.782a3.75 3.75 0 0 1 1.323-1.076 6.258 6.258 0 0 1 1.711-.547 16.64 16.64 0 0 1 1.693-.194c.177-.018.336-.018.512-.018h.459v-.388c0-.546-.142-.935-.441-1.182-.3-.229-.777-.352-1.43-.352a4.32 4.32 0 0 0-1.64.3c-.476.194-.952.423-1.411.688a2.894 2.894 0 0 1-.476-.812c-.124-.3-.177-.511-.177-.653 0-.194.106-.388.336-.582.229-.194.529-.353.917-.511.388-.16.829-.265 1.34-.37a9.01 9.01 0 0 1 1.588-.142c.776 0 1.429.07 1.958.23.53.158.97.405 1.305.723a2.8 2.8 0 0 1 .724 1.234c.14.494.211 1.076.211 1.73l.018 4.074Zm-2.752-2.487h-.3c-.106 0-.194 0-.3.017-.352.036-.705.071-1.058.124a2.92 2.92 0 0 0-.935.3 2.083 2.083 0 0 0-.67.564c-.177.23-.265.53-.265.882 0 .406.106.741.335 1.023.23.283.618.424 1.165.424.264 0 .529-.035.776-.106a2.43 2.43 0 0 0 .652-.282c.194-.124.336-.247.441-.37.106-.142.16-.265.16-.371v-2.205Zm8.097-4.234c.23-.264.582-.547 1.076-.829.494-.282 1.094-.423 1.834-.423.53 0 1.041.088 1.518.264.476.177.9.477 1.27.865.37.405.67.935.882 1.587.229.653.335 1.464.335 2.435 0 1.111-.124 2.046-.388 2.77-.265.722-.582 1.305-.988 1.71a3.52 3.52 0 0 1-1.358.9 4.48 4.48 0 0 1-1.5.264c-.405 0-.776-.052-1.076-.14a3.548 3.548 0 0 1-.776-.318 2.383 2.383 0 0 1-.494-.335 1.77 1.77 0 0 1-.212-.23v5.292h-2.928V14.448c.07-.017.159-.035.265-.035.088-.018.194-.018.3-.035.123 0 .247-.018.405-.018.477 0 .882.07 1.218.23.317.158.529.423.617.793Zm3.88 3.97c0-.424-.035-.83-.105-1.2a3.161 3.161 0 0 0-.353-.97 1.788 1.788 0 0 0-.67-.636c-.283-.158-.618-.229-1.023-.229-.6 0-1.023.124-1.253.335-.23.23-.353.477-.353.759v4.569c.106.123.318.246.618.405.3.159.635.23 1.023.23.723 0 1.252-.283 1.605-.865.335-.564.512-1.358.512-2.399Zm5.293 3.704a3.71 3.71 0 0 1-.194-.9c-.036-.318-.036-.635-.036-.953v-9.966a1.91 1.91 0 0 1 .318-.035c.106-.018.212-.018.317-.036.106 0 .23-.017.336-.017.229 0 .458.017.688.07.229.035.44.124.617.265.194.123.335.3.459.529.105.23.176.511.176.864v1.535h3.016v2.17h-2.998v4.586c0 1.006.405 1.5 1.217 1.5.194 0 .388-.036.564-.089.177-.07.335-.14.477-.211a1.93 1.93 0 0 0 .37-.247c.106-.088.176-.16.23-.194.211.317.387.582.493.811.106.23.177.441.177.653 0 .194-.07.37-.23.547a2.34 2.34 0 0 1-.635.494c-.264.158-.6.282-.988.37a5.713 5.713 0 0 1-1.27.141c-.882 0-1.587-.176-2.081-.511-.459-.37-.812-.812-1.023-1.376Zm13.106-.283c.353 0 .67-.035.953-.123.264-.07.511-.177.74-.282a3.47 3.47 0 0 0 .565-.37c.159-.142.3-.265.424-.371.176.211.352.494.546.829.194.335.283.617.283.829 0 .317-.177.6-.53.864-.3.23-.723.424-1.287.6-.547.159-1.2.247-1.94.247a7.64 7.64 0 0 1-1.994-.265 4.581 4.581 0 0 1-1.746-.9c-.512-.423-.918-1.005-1.235-1.728-.318-.723-.477-1.64-.477-2.734 0-.9.142-1.676.424-2.329.282-.652.653-1.2 1.111-1.623.459-.423.988-.74 1.588-.952a5.662 5.662 0 0 1 1.8-.3c.793 0 1.48.123 2.045.37A3.602 3.602 0 0 1 77 15.543c.352.423.6.9.758 1.446a6.07 6.07 0 0 1 .23 1.712v.3c0 .123 0 .229-.018.37 0 .123-.018.247-.018.37 0 .124-.017.212-.035.283h-6.333c.053.793.3 1.446.741 1.975.423.512 1.111.776 2.046.776Zm.988-4.357c0-.688-.141-1.252-.406-1.676-.264-.423-.74-.652-1.428-.652-.6 0-1.059.212-1.412.617-.352.406-.529.97-.529 1.711h3.775Zm7.726-2.98c.089-.124.194-.265.336-.424.14-.159.317-.3.529-.424.211-.123.44-.229.688-.317.247-.088.511-.124.811-.124.177 0 .353.018.547.036.194.035.37.088.53.158.158.071.3.177.405.318.106.141.159.3.159.494 0 .317-.07.67-.23 1.076a8.483 8.483 0 0 1-.476 1.04 3.76 3.76 0 0 0-.723-.458 2.16 2.16 0 0 0-.864-.159c-.442 0-.812.124-1.112.388-.3.265-.459.565-.459.935v6.95h-2.928V14.431a1.07 1.07 0 0 1 .265-.036c.088-.017.194-.017.3-.035.106 0 .247-.018.406-.018.458 0 .864.089 1.199.265.317.194.53.459.617.83Zm8.309 0c.088-.124.194-.265.335-.424.141-.159.318-.3.53-.424.21-.123.44-.229.687-.317.247-.088.512-.124.812-.124.176 0 .352.018.546.036.195.035.37.088.53.158.158.071.3.177.405.318.106.141.16.3.16.494 0 .317-.071.67-.23 1.076a8.468 8.468 0 0 1-.477 1.04 3.76 3.76 0 0 0-.723-.458 2.161 2.161 0 0 0-.864-.159c-.441 0-.811.124-1.111.388-.3.265-.459.565-.459.935v6.95h-2.928V14.431a1.07 1.07 0 0 1 .264-.036c.088-.017.194-.017.3-.035.106 0 .247-.018.406-.018.459 0 .864.089 1.2.265.317.194.529.459.617.83Zm13.953 6.667c0 1.129.159 1.958.494 2.47a3.283 3.283 0 0 1-.723.211 4.48 4.48 0 0 1-.706.07c-.511 0-.899-.105-1.164-.299-.265-.194-.441-.494-.512-.864-.317.335-.723.617-1.252.846-.512.23-1.147.335-1.888.335a4.41 4.41 0 0 1-1.164-.158 2.99 2.99 0 0 1-1.058-.53c-.318-.246-.565-.564-.776-.97-.194-.405-.3-.9-.3-1.481 0-.724.176-1.323.529-1.782a3.75 3.75 0 0 1 1.323-1.076 6.259 6.259 0 0 1 1.711-.547 16.64 16.64 0 0 1 1.693-.194c.177-.018.336-.018.512-.018h.459v-.388c0-.546-.141-.935-.441-1.182-.3-.229-.776-.352-1.429-.352-.618 0-1.164.105-1.64.3a11.48 11.48 0 0 0-1.412.688 2.894 2.894 0 0 1-.476-.812c-.124-.3-.177-.511-.177-.653 0-.194.106-.388.336-.582.229-.194.529-.353.917-.511.388-.16.829-.265 1.34-.37a9.008 9.008 0 0 1 1.588-.142c.776 0 1.429.07 1.958.23.529.158.97.405 1.306.723.335.335.582.74.723 1.234.141.494.211 1.076.211 1.73l.018 4.074Zm-2.752-2.487h-.3c-.105 0-.194 0-.3.017-.352.036-.705.071-1.058.124a2.92 2.92 0 0 0-.935.3 2.085 2.085 0 0 0-.67.564c-.177.23-.265.53-.265.882 0 .406.106.741.335 1.023.23.283.618.424 1.165.424.264 0 .529-.035.776-.106.247-.07.458-.159.652-.282a1.93 1.93 0 0 0 .441-.37c.106-.142.159-.265.159-.371v-2.205Z"></path></g><defs><clipPath id="a-1"><path fill="#fff" d="M0 0h105.84v36H0z"></path></clipPath></defs></svg>
                                        <div className='upgadeify-capterra-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                    </div>
                                    )}
                                    <div className='upgadeify-capterra-reviews-header-rating'>
                                        {Number(header_rating) === 1 && (
                                        <>
                                            <div className="upgadeify-capterra-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                            <div className="upgadeify-capterra-reviews-item-rating">
                                                <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                            </div>
                                        </>
                                        )}
                                        {Number(header_number_of_reviews) === 1 && (
                                            <span className="upgadeify-capterra-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                        )}
                                    </div>
                                </div>
                                {Number(header_write_reviews_button) === 1 && (
                                    <div className='upgadeify-capterra-reviews-header-right'>
                                        <a
                                            href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='upgadeify-capterra-reviews-header-button'
                                            style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on Capterra
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}

                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {/* Load More Button  */}
                        {visibleListLayoutCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                            {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {visibleGridCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                            
                        </div>
                            {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Capterra reviews found please try.</p>
                </div>
            )}
        </div >
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-capterra-reviews">
            <div className='upgadeify-capterra-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
                <div className='upgadeify-capterra-reviews-head'>
                    {(Number(reviewTabData.review_is_show_reviewer_picture) === 1 || Number(reviewTabData.review_is_show_verified_badge) === 1) && (
                        <div className="upgadeify-capterra-reviews-item-image">
                            {review.image ? (
                                <img src={review.image} alt="Review" />
                            ) : (
                                <div className="fallback-avatar">
                                    {review.name.charAt(0)}
                                </div>
                            )}
                           
                        </div>
                    )}
                    <div className='upgadeify-capterra-reviews-item-name'>
                        {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                                <a
                                    href={review.author_url} 
                                    target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                    rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                    style={{ color: reviewTabData.style_review_reviewer_name_color }}
                                >
                                    <span className="upgadeify-capterra-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                                </a>
                            ) : (
                                <span className="upgadeify-capterra-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                    {review.name}
                                </span>
                            )
                        )}

                        {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                                <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                            </svg>
                        )}

                        <span className="upgadeify-capterra-reviews-item-date">
                            {Number(reviewTabData.review_is_show_date) === 1 && (
                                <span style={{color: reviewTabData.style_review_date_color,}}>{review.review_date ? format(new Date(review.review_date), 'yyyy-MM-dd') : ''}</span>
                            )}
                        </span>
                    </div>
                </div>
                <div className="upgadeify-capterra-reviews-item-content">
                   {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-capterra-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-capterra-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-capterra-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                    )}

                    {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                        <div className='upgadeify-capterra-reviews-item-image-badge'>
                           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 25" className="injected-svg"><path fill="#FF9D28" d="M2.5 10.585h11.954V6.11L2.5 10.585Z"></path><path fill="#68C5ED" d="M14.454 6.11v15.82L20.106 4l-5.652 2.11Z"></path><path fill="#044D80" d="M14.454 10.585H9.937l4.517 11.332V10.585Z"></path><path fill="#E54747" d="m2.5 10.585 8.6 2.908-1.163-2.908H2.5Z"></path></svg>
                            Posted On   
                            <> <a href={`https://www.capterra.com`} target="blank">Capterra</a></> 
                        </div> 
                    )} 
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
